import React from 'react';
import PropTypes from 'prop-types';
import { Box, keyframes } from '@mui/system';
import { alpha, useTheme } from '@mui/material/styles';

import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckIcon from '@mui/icons-material/Check';

import {
  DIVIDER, MUTED, PILL_BORDER, PILL_TEXT, SANS_STACK, SUCCESS,
} from '../tokens';
import { sourceColor, sourceShortName } from '../sources';

const UNBRANDED_ORIGINS = ['web', 'schema'];

export const SUGGEST_ICON_SIZE = 15;

function badgeStyle(src, coral) {
  if (UNBRANDED_ORIGINS.includes(src)) {
    return { label: 'page', bg: DIVIDER, color: PILL_TEXT, border: DIVIDER };
  }
  const label = sourceShortName(src);
  const brand = sourceColor(src);
  if (!brand) return { label, bg: '#fff', color: coral, border: coral };
  return { label, bg: brand, color: '#fff', border: brand };
}

const shimmer = keyframes`
  from { background-position: 120% 0; }
  to { background-position: -120% 0; }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: none; }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

// Coral-tinted so a panel reads as attached to the field above it without
// competing with that field's own focus ring.
const panelSx = (coral) => ({
  border: `1px solid ${alpha(coral, 0.35)}`,
  background: alpha(coral, 0.03),
  borderRadius: '10px',
  padding: '10px 12px',
  animation: `${fadeUp} .25s ease`,
});

const headerLabelSx = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '.08em',
  textTransform: 'uppercase',
};

function SourceBadge({ src }) {
  const coral = useTheme().palette.primary.main;
  const source = badgeStyle(src, coral);
  return (
    <Box
      component="span"
      sx={{
        flexShrink: 0,
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: '.05em',
        textTransform: 'uppercase',
        padding: '3px 6px',
        borderRadius: '4px',
        lineHeight: 1,
        background: source.bg,
        color: source.color,
        border: `1px solid ${source.border}`,
      }}
    >
      {source.label}
    </Box>
  );
}

SourceBadge.propTypes = { src: PropTypes.string.isRequired };

/**
 * One candidate. `children` renders the value, so each field can set it in its
 * own type — the title in the serif, a cover as a thumbnail.
 */
export function SuggestionRow({
  candidate, applied, appliedLabel, onPick, children,
}) {
  const coral = useTheme().palette.primary.main;
  return (
    <Box
      component="button"
      type="button"
      onClick={() => onPick(candidate)}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '9px',
        width: '100%',
        textAlign: 'left',
        border: `1px solid ${applied ? SUCCESS : PILL_BORDER}`,
        background: applied ? alpha(SUCCESS, 0.07) : '#fff',
        borderRadius: '8px',
        padding: '9px 11px',
        cursor: 'pointer',
        fontFamily: SANS_STACK,
        '&:hover': { borderColor: applied ? SUCCESS : coral },
      }}
    >
      <Box sx={{ mt: '2px' }}><SourceBadge src={candidate.src} /></Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {children}
        {candidate.reason && (
          <Box sx={{ fontSize: 11, color: MUTED, mt: '3px' }}>{candidate.reason}</Box>
        )}
      </Box>
      {applied && (
        <Box sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          color: SUCCESS,
          fontSize: 11,
          fontWeight: 700,
          flexShrink: 0,
          mt: '2px',
        }}>
          <CheckIcon sx={{ fontSize: 13 }} />
          {appliedLabel}
        </Box>
      )}
    </Box>
  );
}

SuggestionRow.propTypes = {
  candidate: PropTypes.object.isRequired,
  applied: PropTypes.bool,
  appliedLabel: PropTypes.string,
  onPick: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};

SuggestionRow.defaultProps = {
  applied: false,
  appliedLabel: 'Applied',
};

// Stands in for the button's icon while its request is out.
export function SuggestionSpinner() {
  const coral = useTheme().palette.primary.main;
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        width: SUGGEST_ICON_SIZE,
        height: SUGGEST_ICON_SIZE,
        border: `2px solid ${alpha(coral, 0.25)}`,
        borderTopColor: coral,
        borderRadius: '50%',
        animation: `${spin} .8s linear infinite`,
      }}
    />
  );
}

export function SuggestionLabel({ busy, label, busyLabel }) {
  return (
    <Box component="span" sx={{ display: 'grid' }}>
      <Box component="span" sx={{ gridArea: '1 / 1', visibility: busy ? 'hidden' : 'visible' }}>
        {label}
      </Box>
      <Box component="span" sx={{ gridArea: '1 / 1', visibility: busy ? 'visible' : 'hidden' }}>
        {busyLabel}
      </Box>
    </Box>
  );
}

SuggestionLabel.propTypes = {
  busy: PropTypes.bool,
  label: PropTypes.string,
  busyLabel: PropTypes.string,
};

SuggestionLabel.defaultProps = {
  busy: false,
  label: 'Suggest',
  busyLabel: 'Suggesting…',
};

// The placeholder a field shows while Suggest is in flight.
export function SuggestionSkeleton({ height, count }) {
  return (
    <>
      {Array.from({ length: count }, (unused, i) => i).map((i) => (
        <Box
          key={i}
          sx={{
            height,
            borderRadius: '8px',
            mt: i === 0 ? '8px' : '6px',
            background: 'linear-gradient(90deg,#f0f1f3 25%,#f8f9fa 40%,#f0f1f3 60%)',
            backgroundSize: '200% 100%',
            animation: `${shimmer} 1.2s linear infinite`,
          }}
        />
      ))}
    </>
  );
}

SuggestionSkeleton.propTypes = {
  height: PropTypes.number,
  count: PropTypes.number,
};

SuggestionSkeleton.defaultProps = {
  height: 38,
  count: 1,
};

// Shown in place of a panel when every source already agrees with what the
// field holds.
export function SuggestionAgrees() {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '3px',
        color: SUCCESS,
        fontSize: 11,
        fontWeight: 700,
        textTransform: 'none',
        letterSpacing: 0,
        ml: '8px',
      }}
    >
      <CheckIcon sx={{ fontSize: 12 }} />
      looks good
    </Box>
  );
}

/**
 * The card of candidates under an edit field. Renders nothing without
 * candidates, so a caller can mount it unconditionally.
 */
export default function SuggestionPanel({
  label, candidates, appliedId, isApplied, appliedLabel, onPick, onRestore, renderValue, sx,
}) {
  const coral = useTheme().palette.primary.main;
  if (!candidates || !candidates.length) return null;

  return (
    <Box sx={{ ...panelSx(coral), ...sx }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <AutoAwesomeIcon sx={{ fontSize: 13, color: coral }} />
        <Box component="span" sx={{ ...headerLabelSx, color: coral }}>{label}</Box>
        {appliedId && onRestore && (
          <Box
            component="button"
            type="button"
            onClick={onRestore}
            sx={{
              border: 'none',
              background: 'none',
              color: MUTED,
              fontSize: 11,
              textDecoration: 'underline',
              cursor: 'pointer',
              p: 0,
              ml: '6px',
              fontFamily: SANS_STACK,
              '&:hover': { color: coral },
            }}
          >
            restore original
          </Box>
        )}
      </Box>
      <Box sx={{
        display: 'flex', flexDirection: 'column', gap: '6px', mt: '6px',
      }}>
        {candidates.map((candidate) => (
          <SuggestionRow
            key={candidate.id}
            candidate={candidate}
            applied={isApplied ? isApplied(candidate) : appliedId === candidate.id}
            appliedLabel={appliedLabel}
            onPick={onPick}
          >
            {renderValue(candidate)}
          </SuggestionRow>
        ))}
      </Box>
    </Box>
  );
}

SuggestionPanel.propTypes = {
  label: PropTypes.string,
  candidates: PropTypes.array,
  // The candidate currently written into the draft, if any.
  appliedId: PropTypes.string,
  // Per-row alternative to appliedId, for a multi-select field: authors add up
  // rather than replacing one another, several rows can be applied at once.
  isApplied: PropTypes.func,
  appliedLabel: PropTypes.string,
  onPick: PropTypes.func.isRequired,
  onRestore: PropTypes.func,
  renderValue: PropTypes.func.isRequired,
  sx: PropTypes.object,
};

SuggestionPanel.defaultProps = {
  label: 'Suggestions',
  candidates: [],
  appliedId: null,
  isApplied: undefined,
  appliedLabel: 'Applied',
  onRestore: undefined,
  sx: null,
};
